import * as echarts from "echarts";
import React, { useEffect, useRef } from "react";
import { YoYPoint } from "src/helpers";

export const formatFYLabel = (
  fy: string | number | undefined | null,
): string => {
  if (!fy) return "";
  return String(fy)
    .replace(/^FY\s*/i, "")
    .trim();
};

export const isCurrentFinancialYear = (
  fy: string | number | undefined | null,
): boolean => {
  if (!fy) return false;
  const clean = formatFYLabel(fy);
  const now = new Date();
  // Financial Year in India runs from April 1 (month index 3) to March 31
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const startYear = currentMonth >= 3 ? currentYear : currentYear - 1;
  const endYear = startYear + 1;

  const shortFormat = `${String(startYear).slice(-2)}-${String(endYear).slice(-2)}`;
  const fullFormat = `${startYear}-${endYear}`;
  const mixedFormat = `${startYear}-${String(endYear).slice(-2)}`;

  return (
    clean === shortFormat ||
    clean === fullFormat ||
    clean === mixedFormat ||
    clean.endsWith(shortFormat)
  );
};

const YOYChart = ({
  data,
  darkMode,
  isExpand,
  isAnalyzeStock,
}: {
  data: YoYPoint[];
  darkMode: boolean;
  isExpand?: boolean;
  isAnalyzeStock?: boolean;
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.EChartsType | null>(null);
  const years = data.map((item) => formatFYLabel(item.fy_year));
  const growthValues = data.map((item) => Number(item.growthavg));
  const isCompact = Boolean(isAnalyzeStock && !isExpand);

  const option = {
    backgroundColor: "transparent",
    tooltip: {
      trigger: "axis",
      formatter: (params: { axisValue: string; data: number }[]) =>
        `${params[0].axisValue} : ${params[0].data}%`,
    },
    grid: {
      top: isCompact ? 24 : 30,
      left: 50,
      right: 10,
      bottom: isCompact ? 28 : 25,
      containLabel: false,
    },
    graphic:
      data.length > 0
        ? [
            {
              id: "fy-axis-label",
              type: "text",
              left: isCompact ? 14 : 18,
              bottom: isCompact ? 8 : 6,
              style: {
                text: "FY",
                fill: darkMode ? "#ccc" : "#333",
                fontSize: isCompact ? 10 : 12,
              },
              z: 10,
            },
          ]
        : [],
    xAxis: {
      type: "category",
      data: years,
      axisLine: { lineStyle: { color: darkMode ? "#ccc" : "#333" } },
      axisTick: { show: false },
      axisLabel: {
        show: true,
        showMaxLabel: true,
        showMinLabel: true,
        hideOverlap: true,
        margin: 8,
        color: darkMode ? "#ccc" : "#333",
        fontSize: isCompact ? 10 : 12,
        interval: (index: number) => {
          if (index === years.length - 1) return true;
          if (index === 0) return true;
          const step = isCompact ? 3 : 2;
          return index % step === 0 && years.length - 1 - index >= 2;
        },
        formatter: (val: string, index: number) => {
          if (index === years.length - 1 || isCurrentFinancialYear(val)) {
            return `{current|${val}}`;
          }
          return `{normal|${val}}`;
        },
        rich: {
          normal: {
            color: darkMode ? "#ccc" : "#333",
            fontSize: isCompact ? 10 : 12,
          },
          current: {
            color: darkMode ? "#38bdf8" : "#0284c7",
            fontSize: isCompact ? 10 : 12,
            textShadowColor: darkMode
              ? "rgba(0, 0, 0, 0.7)"
              : "rgba(255, 255, 255, 0.8)",
            textShadowBlur: 1.5,
            textShadowOffsetY: 1,
          },
        },
      },
    },
    yAxis: {
      type: "value",
      scale: true,
      splitLine: { show: false },
      axisLine: { lineStyle: { color: darkMode ? "#ccc" : "#333" } },
      axisLabel: { formatter: (val: number) => `${val}%` },
    },
    series: [
      {
        name: "YoY Return",
        type: "line",
        smooth: true,
        data: growthValues,
        lineStyle: { color: "#999", width: 1.5 },
        areaStyle: {
          color: "rgba(150, 150, 150, 0.3)",
        },
        symbol: "circle",
        symbolSize: isCompact ? 4 : 6,
        itemStyle: {
          color: (params: { value: number }) =>
            params.value >= 0 ? "#FFD700" : "#FF4C4C",
        },
        label: {
          show: true,
          position: "top",
          fontSize: isCompact ? 9 : 10,
          color: darkMode ? "#ffffff" : "#222222",
          formatter: (params: { value: number }) =>
            `${Number(params.value).toFixed(1)}%`,
        },
        labelLayout: { hideOverlap: true },
      },
    ],
  };

  useEffect(() => {
    if (chartRef.current) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(
          chartRef.current,
          darkMode ? "dark" : "light",
        );
      }
      chartInstance.current.setOption(option, true);
      chartInstance.current.resize();
    }

    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener("resize", handleResize);
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && chartRef.current
        ? new ResizeObserver(handleResize)
        : null;
    if (chartRef.current && resizeObserver) {
      resizeObserver.observe(chartRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserver?.disconnect();
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, darkMode, isAnalyzeStock, isExpand]);

  return (
    <div
      ref={chartRef}
      id="yoy-chart"
      className={`w-full ${isExpand || isAnalyzeStock ? "h-full min-h-0" : "h-[30vh]"}`}
    />
  );
};

export default React.memo(YOYChart);
